'use client';

import React, { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import {
  selectLoginToken,
  selectMobile,
  selectResetLogin,
  useAuthStore,
} from '@/store/use-auth.store';
import { Typography } from 'antd';
import { Formik, Form as FormikForm, type FormikProps } from 'formik';
import { useRouter } from 'nextjs-toploader/app';
import * as Yup from 'yup';
import { type LoginMutationParams } from '@/common/apis/identity/v1/login.api';
import Button from '@/common/components/button';
import { FormikNumericInput } from '@/common/components/input';
import Toast from '@/common/components/toast/toast.component';
import { useSession } from '@/common/hooks/use-session.hook';
import { manualLogin } from '@/common/utils/auth-actions.client';
import { configSentry } from '@/common/utils/config-sentry.utility';
import { toEnglishDigits } from '@/common/utils/to-english-digits.utility';

const { Text } = Typography;

const Resend = dynamic(async () => await import('./resend.component'), {
  ssr: false,
});

const validationSchema = Yup.object().shape({
  otpValue: Yup.number().required('کد تایید الزامی است'),
});

interface OTPFormProps {
  endOperation: () => void;
  setMobile: () => void;
}
export default function OTPForm({ endOperation, setMobile }: OTPFormProps) {
  const loginToken = useAuthStore(selectLoginToken);
  const mobile = useAuthStore(selectMobile);
  const reset = useAuthStore(selectResetLogin);
  const [otpSms, setOtpSms] = useState('');

  const [pending, setPending] = useState(false);
  const inFlightRef = useRef(false);
  const succeededRef = useRef(false);
  const router = useRouter();
  const formikRef = useRef<FormikProps<LoginMutationParams>>(null);

  const { refresh } = useSession();

  const onSubmitHandler = async (values: LoginMutationParams) => {
    if (inFlightRef.current || succeededRef.current) return;

    const phoneNumber = mobile;
    const token = loginToken;
    const otpValue = toEnglishDigits(String(values.otpValue ?? ''));

    if (!phoneNumber || !token || !otpValue) {
      return;
    }

    inFlightRef.current = true;
    setPending(true);

    try {
      const res = await manualLogin({
        phoneNumber,
        otpValue,
        token,
      });

      if (!res.ok) {
        Toast.error(res.error || 'خطایی در ورود به اپلیکیشن رخ داد.');
        return;
      }

      succeededRef.current = true;
      endOperation();
      configSentry({ mobileNumber: phoneNumber });
      reset();
      await refresh();
      router.refresh();
    } finally {
      inFlightRef.current = false;
      setPending(false);
    }
  };

  useEffect(() => {
    const ac = new AbortController();
    if (navigator.credentials)
      navigator?.credentials
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        .get({
          otp: { transport: ['sms'] },
          signal: ac.signal,
        } as any)
        .then((otp: any) => {
          if (succeededRef.current || inFlightRef.current) return;
          setOtpSms(otp.code as string);
          formikRef.current?.setFieldValue('otpValue', otp?.code as string);
        })
        .catch(() => undefined);

    return () => {
      ac.abort();
    };
  }, []);

  return (
    <div className="h-full w-full">
      <Formik
        initialValues={{
          otpValue: otpSms || '',
          phoneNumber: '',
          token: '',
        }}
        validationSchema={validationSchema}
        onSubmit={onSubmitHandler}
        innerRef={formikRef}
        enableReinitialize
      >
        {({ errors }) => (
          <FormikForm className="relative" autoComplete="off">
            <Text
              dir="rtl"
              className="block w-full text-right text-base font-medium text-[#191308]"
            >
              کد ارسال شده به شماره{' '}
              <bdi dir="ltr" className="whitespace-nowrap">
                {mobile}
              </bdi>{' '}
              را وارد کنین.
            </Text>
            <FormikNumericInput
              maxLength={4}
              type="tel"
              id="otpValue"
              label="کد تایید"
              disabled={pending}
              name="otpValue"
              className={`!my-0 w-full text-center`}
              error={errors.otpValue}
              autoComplete="one-time-code"
            />
            <Resend onEditPhoneNumber={setMobile} />
            <div className="mb-4 mt-2 flex items-center gap-3 rounded-2xl border border-[#CBEFE4] bg-[#F3FCF9] p-3.5 shadow-[0_4px_16px_rgba(0,168,132,0.08)]">
              <Image
                src="/assets/images/webp/bale-logo.webp"
                alt=""
                width={36}
                height={36}
                className="h-9 w-9 shrink-0 object-contain"
              />
              <Text className="text-xs leading-5 text-black-300">
                اگه کد پیامکی رو دریافت نکردین، کد رو از طریق بله هم براتون
                می‌فرستیم.
              </Text>
            </div>
            <Button
              type="black"
              className="!mt-0 w-full"
              htmlType="submit"
              loading={pending}
            >
              تایید
            </Button>
          </FormikForm>
        )}
      </Formik>
    </div>
  );
}
